import React from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import useReveal from "../hooks/useReveal";
import { useLang } from "../i18n/LanguageContext";
import { useLocalizedPath, cityPath } from "../i18n/langPath";
import { locStack } from "../data/localizeContent";

// Grosse, vertikal gestapelte Bilder (eigene Fotos) — je Block ein Ziel/Service.
const STACK = [
  { img: "/images/a_0902.webp", title: "Flughafentransfer Zürich", sub: "Festpreis ab ZRH", to: "/blog/flughafentransfer-zuerich" },
  { img: "/images/a_1583.jpeg", title: "Flughafentaxi Zürich", sub: "24/7 mit Flugverfolgung", to: "/blog/airport-taxi-zuerich" },
  { img: "/images/a_1586.jpeg", title: "Flughafentransfer Bern", sub: "Bern · Genf · Basel", to: "/blog/flughafentransfer-bern" },
  { img: "/images/a_1587.webp", title: "Flughafentransfer Basel", sub: "Basel · EuroAirport · ZRH", to: "/blog/flughafentransfer-basel" },
  { img: "/images/a_1588.jpeg", title: "Flughafentransfer Zürich", sub: "Direkt vom Terminal", city: "zuerich" },
  { img: "/images/a_1589.jpeg", title: "Geschäftsreisen mit Chauffeur", sub: "Diskret & pünktlich", to: "/blog/geschaeftsreisen-chauffeur" },
  { img: "/images/a_1590.webp", title: "Flughafentransfer Interlaken", sub: "Berner Oberland", city: "interlaken" },
  { img: "/images/a_1908.webp", title: "Stadt zu Stadt", sub: "Komfortabel zwischen Städten", to: "/blog/stadt-zu-stadt-transfer" },
  { img: "/images/a_2647.webp", title: "Flughafentransfer St. Moritz", sub: "Engadin · Alpentransfer", city: "st-moritz" },
  { img: "/images/a_2648.webp", title: "Messetransfers", sub: "Basel · Zürich · Genf", to: "/blog/messe-transfer-basel-zuerich" },
  { img: "/images/a_2650.webp", title: "Flughafentransfer nach Alpen", sub: "Zermatt · Verbier · Davos", to: "/blog/alpen-transfer-winter" },
  { img: "/images/a_2651.webp", title: "Gruppentransfer", sub: "Vans & Sprinter bis 16 Pers.", to: "/blog/gruppentransfer-vans" },
];

export default function Destinations() {
  useReveal();
  const { t, lang } = useLang();
  const lp = useLocalizedPath();
  const linkTo = (s) => (s.city ? cityPath(s.city, lang) : lp(s.to));
  return (
    <section id="destinations" className="py-16 md:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-5 md:px-8 text-center reveal">
        <span className="text-xs uppercase tracking-[0.2em] text-[#F5B400] font-semibold">{t("dest_eyebrow")}</span>
        <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-black leading-tight">{t("dest_title")}</h2>
      </div>

      <div className="mt-12 flex flex-col gap-6 md:gap-8 max-w-5xl mx-auto px-5 md:px-8">
        {STACK.map((raw, i) => {
          const s = locStack(raw, lang);
          return (
          <Link
            key={s.img}
            to={linkTo(s)}
            className="reveal group relative block w-full overflow-hidden rounded-3xl shadow-sm"
            style={{ animationDelay: `${(i % 4) * 0.06}s` }}
            data-testid={`destination-stack-${i}`}
          >
            <img
              src={s.img}
              alt={s.title}
              loading="lazy"
              decoding="async"
              width="1100"
              height="620"
              className="block w-full h-[340px] sm:h-[460px] md:h-[560px] object-cover transition-transform duration-[900ms] group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <div className="flex items-center gap-2 text-[#FCD34D]">
                <MapPin className="w-4 h-4" />
                <span className="text-xs md:text-sm uppercase tracking-[0.15em] font-semibold">{s.sub}</span>
              </div>
              <div className="mt-2 flex items-center justify-between gap-4">
                <h3 className="font-display text-2xl md:text-4xl font-bold text-white">{s.title}</h3>
                <span className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-white border border-white/40 rounded-full px-5 py-2.5 backdrop-blur-sm group-hover:bg-white group-hover:text-black transition-colors">
                  {t("dest_learn_more")} <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </div>
          </Link>
          );
        })}
      </div>

      <div className="mt-12 text-center reveal">
        <Link to={lp("/staedte")} className="inline-flex items-center gap-2 text-sm font-semibold text-black border border-slate-200 rounded-full px-6 py-3 hover:border-[#F5B400] hover:text-[#F5B400] transition-colors" data-testid="destinations-all">
          {t("dest_all")} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}
